/**
 * Centralized application configuration
 *
 * All environment variables and configuration values should be accessed through
 * this module to ensure consistency and runtime validation.
 */

/**
 * Get the base URL for the API backend
 * @throws {Error} If VITE_APP_BASE_URL is not defined
 */
export function getApiBaseUrl(): string {
  const url = import.meta.env.VITE_APP_BASE_URL;
  // Empty string means use relative paths (go through Vite proxy in dev)
  // This is preferred for local development as cookies flow correctly
  if (url === undefined) {
    throw new Error(
      'VITE_APP_BASE_URL environment variable is required. ' +
        'Please check your .env file or environment configuration. ' +
        'Set to empty string to use relative paths (recommended for local dev).'
    );
  }
  return url;
}

/**
 * Get the customer app's origin, used by the admin app's sign-in / "Go to Ahoy"
 * links. Customer code never calls this — it's dead code there, so tree-shaking
 * drops it from the customer bundle. The admin build also fails outright without
 * this variable set (see vite.admin.config.ts).
 * @throws {Error} If VITE_APP_FRONTEND_URL is unset or not an absolute http(s) URL
 */
export function getAppFrontendUrl(): string {
  const url = import.meta.env.VITE_APP_FRONTEND_URL;
  if (!url || !/^https?:\/\//i.test(url)) {
    throw new Error(
      'VITE_APP_FRONTEND_URL environment variable is required and must be an ' +
        'absolute http(s) URL (e.g. https://app.ahoy.ai). ' +
        'Please check your .env file or environment configuration.'
    );
  }
  return url.replace(/\/$/, '');
}

/**
 * Get the base URL for Django Allauth API
 * Falls back to {API_BASE_URL}/_allauth/browser/v1 if not explicitly set
 */
export function getAllauthBaseUrl(): string {
  const url = import.meta.env.VITE_ALLAUTH_BASE_URL;
  if (url) {
    return url;
  }
  return `${getApiBaseUrl()}/_allauth/browser/v1`;
}

/**
 * Get Sentry DSN for error tracking
 * Returns undefined if not configured (optional in development)
 */
export function getSentryDsn(): string | undefined {
  return import.meta.env.VITE_SENTRY_DSN;
}

/**
 * Get Sentry environment name
 * Defaults to 'development' if not set
 */
export function getSentryEnvironment(): string {
  return import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';
}

function parseSentrySampleRate(
  value: string | undefined,
  envVarName: string,
  fallback: number
): number {
  if (value === undefined || value === '') {
    return fallback;
  }
  const trimmed = value.trim();
  // Number() (not parseFloat) so standard numeric forms like `.5` and `1e-1`
  // parse correctly, while garbage like `1abc` becomes NaN and is rejected.
  const parsed = Number(trimmed);
  if (trimmed === '' || Number.isNaN(parsed) || parsed < 0 || parsed > 1) {
    console.warn(`Invalid ${envVarName}: ${value}. Using default ${fallback}`);
    return fallback;
  }
  return parsed;
}

/**
 * Get Sentry traces sample rate
 * Defaults to 1.0 (100%) if not set
 */
export function getSentryTracesSampleRate(): number {
  return parseSentrySampleRate(
    import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
    'VITE_SENTRY_TRACES_SAMPLE_RATE',
    1.0
  );
}

/**
 * Get Sentry session-replay sample rate.
 * Defaults to 0.1 (10%) — a conservative production default to control cost.
 * Raise via VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE for debugging.
 */
export function getSentryReplaysSessionSampleRate(): number {
  return parseSentrySampleRate(
    import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
    'VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE',
    0.1
  );
}

/**
 * Get Sentry on-error replay sample rate.
 * Defaults to 1.0 (100%) — always record replays for sessions with errors.
 */
export function getSentryReplaysErrorSampleRate(): number {
  return parseSentrySampleRate(
    import.meta.env.VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE,
    'VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE',
    1.0
  );
}

/**
 * Get Mixpanel project token for analytics
 * Returns undefined if not configured (disabled in development)
 */
export function getMixpanelToken(): string | undefined {
  return import.meta.env.VITE_MIXPANEL_TOKEN;
}

/**
 * Check if the application is running in development mode
 */
export function isDevelopment(): boolean {
  return import.meta.env.DEV;
}

/**
 * Check if the application is running in production mode
 */
export function isProduction(): boolean {
  return import.meta.env.PROD;
}

/**
 * Get the Google Picker API key
 * Used for Google Drive file picker functionality
 */
export function getGooglePickerApiKey(): string | undefined {
  return import.meta.env.VITE_GOOGLE_PICKER_API_KEY;
}

/**
 * Get the Google Client ID
 * Used for Google OAuth and API access
 */
export function getGoogleClientId(): string | undefined {
  return import.meta.env.VITE_GOOGLE_CLIENT_ID;
}

/**
 * Get the Microsoft Client ID
 * Used for Microsoft OAuth and API access
 */
export function getMicrosoftClientId(): string | undefined {
  return import.meta.env.VITE_MICROSOFT_CLIENT_ID;
}

/**
 * Get the build version (git commit SHA) baked in at build time.
 * Returns undefined in development or if not set.
 */
export function getBuildVersion(): string | undefined {
  return import.meta.env.VITE_BUILD_VERSION;
}

/**
 * Get the WebSocket base URL (protocol + host) for real-time connections.
 *
 * When VITE_APP_BASE_URL is set (staging/production with separate frontend
 * and backend services), the WebSocket must connect to the API host — not
 * window.location.host, which points to the static frontend server.
 *
 * When VITE_APP_BASE_URL is empty (local dev behind Vite proxy), falls back
 * to window.location so the proxy can forward the upgrade request.
 */
export function getWebSocketBaseUrl(): string {
  const apiUrl = getApiBaseUrl();
  if (!apiUrl) {
    // Local dev: same origin, use window.location
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}`;
  }
  try {
    const parsed = new URL(apiUrl);
    const protocol = parsed.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${parsed.host}`;
  } catch {
    throw new Error(
      `Invalid VITE_APP_BASE_URL: "${apiUrl}". Set an absolute URL (e.g. https://api.example.com) or empty string for local dev.`
    );
  }
}

/**
 * Validate required configuration on application startup
 * @throws {Error} If any required configuration is missing
 */
export function validateConfig(): void {
  // This will throw if required vars are missing
  getApiBaseUrl();
  getAllauthBaseUrl();

  // Log configuration status in development
  if (isDevelopment()) {
    // NOTE: Cannot use logger here — logger.ts imports from config.ts (circular dep)
    // eslint-disable-next-line no-console -- circular dependency: logger.ts imports from config.ts
    console.log('Configuration validated:', {
      apiBaseUrl: getApiBaseUrl(),
      allauthBaseUrl: getAllauthBaseUrl(),
      sentryConfigured: !!getSentryDsn(),
      environment: getSentryEnvironment(),
    });
  }
}
